/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/
import React, { useState, useEffect, useRef } from 'react'
import { ReactSVG } from 'react-svg'
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateRightIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@material-ui/icons'

import LeftArrow from '../../../../../assets/static/leftArrow.svg'
import RightArrow from '../../../../../assets/static/rightArrow.svg'
import './ImageViewer.css'

import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'
import { formatDateTime } from '../../utils'

const ImageViewer = ({
  images = [],
  currentIndex: controlledIndex,
  onChangeIndex, // notify parent of index change
  onClose,
  allowRotation = true,
  transitTimes,
  showImageName = false,
}) => {
  const isControlled = controlledIndex !== undefined
  const [internalIndex, setInternalIndex] = useState(0)
  const [rotation, setRotation] = useState(0)
  const [fullscreen, setFullscreen] = useState(false)
  const containerRef = useRef(null)
  const transformWrapperRef = useRef(null)

  // Use controlledIndex if controlled, else internal state
  const currentIndex = isControlled ? controlledIndex : internalIndex
  const currentImage = images[currentIndex] || {}
  const shouldShowImageName =
    (fullscreen || showImageName) && currentImage?.name

  // Reset zoom and rotation on image change
  useEffect(() => {
    // reset zoom & pan using transformWrapper's resetTransform
    if (transformWrapperRef.current) {
      transformWrapperRef.current.resetTransform()
    }
    setRotation(0)
  }, [currentIndex])

  // Change image index handler
  const changeIndex = (newIndex) => {
    if (newIndex < 0 || newIndex >= images.length) return
    if (isControlled) {
      onChangeIndex?.(newIndex)
    } else {
      setInternalIndex(newIndex)
    }
  }

  const handlePrev = () => changeIndex(currentIndex - 1)
  const handleNext = () => changeIndex(currentIndex + 1)

  // Rotate
  const rotate = () => {
    if (!allowRotation) return
    setRotation((r) => (r + 90) % 360)
  }

  // Fullscreen toggle
  const toggleFullscreen = () => {
    const el = containerRef.current
    if (!fullscreen && el.requestFullscreen) {
      el.requestFullscreen()
    } else if (document.fullscreenElement) {
      document.exitFullscreen()
    }
  }

  useEffect(() => {
    const onFullScreenChange = () => {
      setFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', onFullScreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', onFullScreenChange)
    }
  }, [])

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target
      const isTypingTarget =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)

      if (isTypingTarget) {
        return
      }

      switch (e.key) {
        case 'ArrowLeft':
          handlePrev()
          break
        case 'ArrowRight':
          handleNext()
          break
        case '+':
        case '=':
          // zoom in via TransformWrapper
          transformWrapperRef.current?.zoomIn()
          break
        case '-':
          // zoom out via TransformWrapper
          transformWrapperRef.current?.zoomOut()
          break
        case 'r':
        case 'R':
          rotate()
          break
        case 'f':
        case 'F':
          toggleFullscreen()
          break
        case 'Escape':
          onClose?.()
          break
        default:
          break
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentIndex, fullscreen])

  return (
    <div
      className="image-viewer-wrapper"
      ref={containerRef}
      data-testid="image-viewer-wrapper"
    >
      <div className="image-viewer">
        <div
          className={`nav-arrow left-arrow ${
            currentIndex === 0 ? 'disabled' : ''
          }`}
          onClick={handlePrev}
          aria-label="Previous image"
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === 'Enter' && handlePrev()}
        >
          <ReactSVG src={LeftArrow} />
        </div>

        <TransformWrapper
          ref={transformWrapperRef}
          options={{
            limitToBounds: true,
            minScale: 0.5,
            maxScale: 3,
            // optional: no wheel zoom here because wheel zoom is automatic by default in react-zoom-pan-pinch
          }}
          wheel={{ step: 0.1 }}
          doubleClick={{ disabled: false }}
        >
          {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
            <TransformComponent
              wrapperStyle={{
                height: '100%',
                width: '100%',
              }}
              contentStyle={{
                height: '100%',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img
                src={currentImage.url}
                alt={currentImage.alt || `Image ${currentIndex + 1}`}
                className="main-image"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 0.3s ease',
                  userSelect: 'none',
                  pointerEvents: 'none',
                  height: '100%',
                  width: '100%',
                }}
                draggable={false}
                data-testid="main-image"
              />
            </TransformComponent>
          )}
        </TransformWrapper>

        <div
          className={`nav-arrow right-arrow ${
            currentIndex === images.length - 1 ? 'disabled' : ''
          }`}
          onClick={handleNext}
          aria-label="Next image"
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === 'Enter' && handleNext()}
        >
          <ReactSVG src={RightArrow} />
        </div>
      </div>

      <div className="viewer-toolbar">
        <div className="image-info" data-testid="image-info">
          {shouldShowImageName && (
            <div className="image-name">{currentImage?.name}</div>
          )}
          {(transitTimes || currentImage?.timestamp) && (
            <div className="image-timestamp">
              {transitTimes || formatDateTime(currentImage?.timestamp)}
            </div>
          )}
        </div>

        <div className="viewer-controls">
          <button
            type="button"
            className="control-button"
            aria-label="Zoom Out"
            onClick={() => transformWrapperRef.current?.zoomOut()}
            // disable if zoom is at minScale (need to track zoom level)
          >
            <ZoomOutIcon />
          </button>

          <button
            type="button"
            className="control-button"
            aria-label="Zoom In"
            onClick={() => transformWrapperRef.current?.zoomIn()}
            // disable if zoom is at maxScale
          >
            <ZoomInIcon />
          </button>

          {allowRotation && (
            <>
              <div className="vertical-separator" aria-hidden="true"></div>
              <button
                type="button"
                className="control-button"
                aria-label="Rotate"
                onClick={rotate}
              >
                <RotateRightIcon />
              </button>
            </>
          )}

          <div className="vertical-separator" aria-hidden="true"></div>

          <button
            type="button"
            className="control-button"
            aria-label={fullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            onClick={toggleFullscreen}
          >
            {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </button>

          {onClose && (
            <button
              type="button"
              className="control-button close-button"
              aria-label="Close Viewer"
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ImageViewer
